import React, { useEffect, useRef, useState } from 'react';
import { useStorm } from './StormContext';

// Slice-1 calibration tool: a corner slider to set the storm's resting floor by FEEL
// while typing a real entry, plus a live FPS readout to confirm the perf floor.
// Shown only when the URL has a ?cal flag (e.g. /write?cal). Not a shipped UI.
function calEnabled(): boolean {
  try {
    return new URLSearchParams(window.location.search).has('cal');
  } catch {
    return false;
  }
}

function useFps(): number {
  const [fps, setFps] = useState(60);
  useEffect(() => {
    let raf = 0;
    let frames = 0;
    let last = performance.now();
    const tick = (t: number) => {
      frames++;
      if (t - last >= 500) {
        setFps(Math.round((frames * 1000) / (t - last)));
        frames = 0;
        last = t;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);
  return fps;
}

const StormCalibrator: React.FC = () => {
  const { restingFloor, setRestingFloor, reducedMotion } = useStorm();
  const fps = useFps();
  const enabled = useRef(calEnabled()).current;
  if (!enabled) return null;

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 14,
        right: 14,
        zIndex: 1000,
        background: 'rgba(0,0,0,0.8)',
        border: '1px solid rgba(255,255,255,0.25)',
        borderRadius: 10,
        padding: '10px 12px',
        color: '#fff',
        font: '11px/1.4 ui-monospace, JetBrains Mono, monospace',
        letterSpacing: '0.04em',
        backdropFilter: 'blur(6px)',
        minWidth: 180,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
        <span>STORM {restingFloor.toFixed(2)}</span>
        <span style={{ color: fps < 55 ? '#e5534b' : '#6fd28a' }}>{fps} fps</span>
      </div>
      <input
        type="range"
        min={0}
        max={1}
        step={0.01}
        value={restingFloor}
        onChange={(e) => setRestingFloor(parseFloat(e.target.value))}
        style={{ width: '100%' }}
      />
      {reducedMotion && (
        <div style={{ marginTop: 6, color: '#e5a44d' }}>reduced-motion → storm off</div>
      )}
    </div>
  );
};

export default StormCalibrator;
